import { pick, serverMap } from '@/shared/lib/server-map'
import type { IconName } from '@/shared/ui/icon'
import type { Messenger } from '@/shared/api'

// Ключ карт ниже — `string`, чтение через `pick`: незнакомый канал уезжает в фолбэк, а не роняет
// строку или плитку (правило — в `entities/booking/model/server-drift.ts`).
const FALLBACK_TOKEN = 'var(--ink-3)'
const FALLBACK_ICON: IconName = 'alert'
const FALLBACK_LABEL = 'Другой канал'

/**
 * `Messenger` → фон плитки аватара.
 *
 * WhatsApp и Telegram — из макета (`docs/reference/phase-2-mockup.md`, разделы 3 и 4). Своего токена
 * у Telegram там нет, он переиспользует токен статуса «подтверждено» — не опечатка, так решено в
 * плане фазы. Email — тоже из макета (`--sunk`); ⚠️ в самой разметке макета иконка при этом покрашена
 * дублирующимся `color` с побеждающим `#fff`, и конверт на почти белом тонет — задокументированный
 * дефект макета, правильный цвет иконки живёт исключением в `ui/MessengerAvatar`.
 *
 * ⚠️ `max`, `vk`, `sms` — НАШ выбор: цвета для них макет не даёт вовсе. `--violet` у `max` взят
 * потому, что не занят под статус брони и не близок по оттенку ни к `--wa`, ни к `--st-confirmed`
 * (в приложении он используется и другими — например, ботом в `channel-kind.ts`). `vk` и `sms` берут
 * статусные: пары «насыщенный и ничем не занятый» на двоих в палитре нет. `--accent` намеренно не
 * берём — им же красится режим «ИИ» в шапке того же треда, и плитка канала читалась бы как «это ИИ».
 */
const MESSENGER_TOKENS = serverMap({
  whatsapp: 'var(--wa)',
  telegram: 'var(--st-confirmed)',
  email: 'var(--sunk)',
  max: 'var(--violet)',
  vk: 'var(--st-pending)',
  sms: 'var(--st-noshow)',
})

export function messengerToken(messenger: Messenger): string {
  return pick(MESSENGER_TOKENS, messenger, FALLBACK_TOKEN)
}

/**
 * `Messenger` → имя иконки. WhatsApp/Telegram/Email — из макета.
 *
 * ⚠️ У `max`, `vk` и `sms` бренд-иконок нет, и подобраны они НАШИМ решением, без рисования новых:
 * `external` («сторонний сервис», как у кнопки «Открыть в мессенджере»), `bell` и `edit` (SMS —
 * «короткое ПИСЬМЕННОЕ»). Ни одна не идеальна, и выбор старше нынешнего набора: сегодня в
 * `icons.tsx` есть `chat`, `phone` и `send`, которые ближе по смыслу, — пересмотр это отдельное
 * решение по виду, а не правка комментария. Незнакомое значение — `alert`, как и везде.
 */
const MESSENGER_ICONS = serverMap<IconName>({
  whatsapp: 'whatsapp',
  telegram: 'telegram',
  email: 'email',
  max: 'external',
  vk: 'bell',
  sms: 'edit',
})

export function messengerIcon(messenger: Messenger): IconName {
  return pick(MESSENGER_ICONS, messenger, FALLBACK_ICON)
}

/**
 * `Messenger` → человекочитаемое название: сервер имени канала не присылает, словарь придуман фронтом.
 *
 * ⚠️ Начнёт присылать — словарь сносим: так уже вышло у площадки, когда имя и цвет приехали с сервера.
 */
const MESSENGER_LABELS = serverMap({
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  max: 'MAX',
  vk: 'VK',
  sms: 'SMS',
  email: 'Email',
})

export function messengerLabel(messenger: Messenger): string {
  return pick(MESSENGER_LABELS, messenger, FALLBACK_LABEL)
}
